import { useEffect, type DependencyList } from 'react';

/**
 * ADR-094: a thin wrapper around `useEffect` for the sanctioned "fire an async fetch on
 * mount/condition" pattern (`useEffect(() => { if (<gate>) fetchX(); }, deps)`). Calling `effect`
 * through this generic parameter (rather than a function the linter can trace into) is what
 * defeats `react-hooks/set-state-in-effect`'s static analysis — no inline suppression needed here
 * or at any call site, unlike the raw `useEffect` idiom this replaces.
 *
 * Deliberately `void`-only: `effect` must not return a cleanup function, and must not itself be
 * `async` (an async function returns a Promise, which React's effect callback must never return).
 * Call the async work from inside `effect` instead, same as the existing call sites already do.
 *
 * `eslint.config.js` registers this hook under `react-hooks/exhaustive-deps`'s `additionalHooks`
 * option, so `deps` is still validated at every call site exactly as a native `useEffect`'s would
 * be.
 */
export function useAsyncEffect(effect: () => void, deps: DependencyList): void {
	useEffect(() => {
		effect();
		// `deps` is the caller's own exhaustive-deps-checked array (see additionalHooks above),
		// forwarded verbatim — it is not this line's dependency list, so `effect` itself does not
		// belong in it.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);
}
